import { Box, Stack, Typography, useTheme } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';

import { DateTime } from '@/utility/luxon';
import { FC } from 'react';

import { TransactionRowInfo } from './transaction-row-info';
import { pxToRem } from '@/theme/typography';
import { useRouter } from 'next/router';
import { checkIfDateIsOlderThanNowDate } from '@/logic/helpers/check-if-date-is-older-than-now-date';
import ImageBoxFuture from '@/components/shared/image-box-future';
import { TransactionType } from '@/features/wallet/services/types';

const CashbackIcon = '/icons/profile/transactions/cashback-icon.svg';
const CashbackRedeemIcon = '/icons/profile/transactions/money-send-icon.svg';

interface TransactionRowProps {
  type: TransactionType;
  amount: number;
  expireDate?: string;
}

export const TransactionRow: FC<TransactionRowProps> = ({ type, amount, expireDate }) => {
  const theme = useTheme();
  const intl = useIntl();
  const { locale } = useRouter();

  const transactionColor = () => {
    if (expireDate && checkIfDateIsOlderThanNowDate(expireDate)) {
      return theme.palette.grey[40];
    } else {
      return amount >= 0 ? theme.palette.toast.success : theme.palette.error.main;
    }
  };

  const getTransactionTitle = (transactionType: TransactionType): string => {
    return intl.formatMessage({
      id: `cashback.transaction.type.${transactionType.toLowerCase()}`,
    });
  };

  const getTransactionImage = (transactionType: TransactionType) => {
    if (transactionType === 'CASHBACK_EARNED') {
      return CashbackIcon;
    }
    if (transactionType === 'CASHBACK_REDEEM') {
      return CashbackRedeemIcon;
    }
  };

  const getTransactionAmount = (amount: number) => {
    return amount >= 0 ? `+${amount.toFixed(2)}` : amount.toFixed(2);
  };

  const getTransactionExpiryDate = (expiryDate: string) => {
    return DateTime.fromISO(expiryDate, {
      locale,
    }).toFormat('DD');
  };

  return (
    <Stack
      direction='row'
      sx={{
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '16px',
        opacity: expireDate && checkIfDateIsOlderThanNowDate(expireDate) ? 0.4 : 1,
      }}
    >
      <Stack
        direction='row'
        sx={{
          alignItems: 'center',
        }}
      >
        <Stack
          direction='row'
          sx={{
            width: '40px',
            height: '40px',
            backgroundColor: theme.palette.grey[40],
            borderRadius: '100%',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '8px',
          }}
        >
          <ImageBoxFuture
            img={getTransactionImage(type)}
            title={intl.formatMessage({
              id: 'alt.transaction.icon',
            })}
            objectFit='contain'
            sx={{
              width: {
                mobile: '40px',
                tablet: pxToRem(33),
              },
              height: {
                mobile: '24px',
                tablet: pxToRem(33),
              },
            }}
          />
        </Stack>

        <Box
          sx={{
            marginLeft: '20px',
          }}
        >
          <Typography
            component='span'
            sx={{
              fontSize: pxToRem(18),
              fontWeight: '700',
            }}
          >
            {getTransactionTitle(type)}
          </Typography>

          {expireDate && (
            <TransactionRowInfo
              title={intl.formatMessage({
                id:
                  expireDate && checkIfDateIsOlderThanNowDate(expireDate)
                    ? 'wallet.expired.at'
                    : 'wallet.expires.on',
              })}
              description={getTransactionExpiryDate(expireDate)}
            />
          )}
        </Box>
      </Stack>

      <Stack
        direction='row'
        sx={{
          alignItems: 'baseline',
        }}
      >
        <Typography
          sx={{
            fontSize: pxToRem(18),
            fontWeight: '700',
            color: transactionColor,
          }}
        >
          {getTransactionAmount(amount)}
        </Typography>
        &nbsp;
        <Typography
          sx={{
            fontSize: pxToRem(16),
            fontWeight: '400',
            color: transactionColor,
          }}
        >
          <FormattedMessage id='currency.SAR' />
        </Typography>
      </Stack>
    </Stack>
  );
};
